import React, { type SVGProps, useEffect, useMemo, useState } from 'react'
import { Box, Chip, Menu, MenuItem, ListItemIcon, ListItemText, Typography, Stack } from '@mui/material'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'

import { useBulkImportStore } from '../../hooks/useBulkImportStore'

import { type CardActivityWithId } from '@/models/card-activity/card-activity.model'
import { useDefaultRubricId } from '@/hooks/useDefaultRubricId'
import { useRubric } from '@/hooks/useRubrics'
import { AUTO_GRADING_PLANS } from '@/constants/plans/variables-plans'
import { withPermissionCheck, type WithPermissionProps } from '@/hoc/WithPermissionCheck'
import { PLGHandler } from '@/components/shadCN/PLGHandler'
import {
  GradingMethodIconRubric,
  GradingMethodIconPassFail,
  GradingMethodIconStandardsBased,
  GradingMethodIconManual,
} from '@/components/GradingMethodIcons'

interface GradingMethodSelectorProps {
  card: CardActivityWithId
  updateCard: (id: string, field: keyof CardActivityWithId, value: CardActivityWithId[keyof CardActivityWithId]) => void
}

interface GradingMethodSelectOptionProps {
  option: {
    icon: React.ReactNode
    menuLabel: React.ReactNode
    value: string
  }
  selectedMethod: string
  handleSelect: (value: string) => void
}

function GradingMethodSelectOption({
  option,
  selectedMethod,
  handleSelect,
  disabled,
  handleOpenPopover,
}: WithPermissionProps<GradingMethodSelectOptionProps>) {
  return (
    <Stack
      alignItems="center"
      direction="row"
      gap="4px"
      justifyContent="space-between"
      width="100%"
      onClick={e => {
        if (disabled) {
          handleOpenPopover?.(e)
          e.preventDefault()
          e.stopPropagation()

          return
        }
        handleSelect(option.value)
      }}
    >
      <Stack alignItems="center" direction="row" gap="4px" width="100%">
        <ListItemIcon>{option.icon}</ListItemIcon>
        <ListItemText primary={option.menuLabel} />
        <ListItemIcon sx={{ ml: 1 }}>
          {option.value === selectedMethod && <CheckRoundedIcon color="primary" fontSize="small" />}
        </ListItemIcon>
        <PLGHandler
          visible={disabled ?? false}
          onMouseEnter={e => {
            handleOpenPopover?.(e)
          }}
        />
      </Stack>
    </Stack>
  )
}

function GradingMethodSelector({ card, updateCard }: GradingMethodSelectorProps): JSX.Element {
  const [selectedMethod, setSelectedMethod] = useState<'rubric' | 'simple' | 'manual' | 'standards_based' | undefined>(
    card?.grading_method ?? 'simple',
  )
  const settings = useBulkImportStore(store => store.settings)
  const defaultRubricId = useDefaultRubricId()

  const rubricToUse = settings.grading.rubricId || defaultRubricId

  const { rubric } = useRubric({
    id: rubricToUse,
    enabled: true,
  })

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const gradingOptions = [
    {
      chipLabel: 'Rubric Grading',
      menuLabel: (
        <Typography color="#101828" fontSize={16} fontWeight={500} lineHeight="24px">
          <span style={{ fontWeight: 600 }}>Rubric Grading:</span> Detailed Scoring
        </Typography>
      ),
      icon: <GradingMethodIconRubric />,
      value: 'rubric' as const,
      // permission: AUTO_GRADING_PLANS.AUTO_GRADING_RUBRICS,
      permission: [],
    },
    {
      chipLabel: 'Pass-Fail',
      menuLabel: (
        <Typography color="#101828" fontSize={16} fontWeight={500} lineHeight="24px">
          <span style={{ fontWeight: 600 }}>Pass-Fail:</span> You Set the Bar
        </Typography>
      ),
      icon: <GradingMethodIconPassFail />,
      value: 'simple' as const,
      // permission: AUTO_GRADING_PLANS.AUTO_GRADING_PASS_FAIL,
      permission: [],
    },
    {
      chipLabel: 'Standards Based',
      menuLabel: (
        <Typography color="#101828" fontSize={16} fontWeight={500} lineHeight="24px">
          <span style={{ fontWeight: 600 }}>Standards Based:</span> Assess by a Standard
        </Typography>
      ),
      icon: <GradingMethodIconStandardsBased />,
      value: 'standards_based' as const,
      // permission: AUTO_GRADING_PLANS.AUTO_GRADING_RUBRICS,
      permission: [],
    },
    {
      chipLabel: 'Manual Grading',
      menuLabel: (
        <Typography color="#101828" fontSize={16} fontWeight={500} lineHeight="24px">
          <span style={{ fontWeight: 600 }}>Manual:</span> Review & Grade Responses
        </Typography>
      ),
      icon: <GradingMethodIconManual />,
      value: 'manual' as const,
      permission: [],
    },
  ]

  const handleOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSelect = (value: 'rubric' | 'simple' | 'manual' | 'standards_based') => {
    updateCard(card.id, 'grading_method', value)
    setSelectedMethod(value)
    if (value === 'rubric') {
      const currentRubric = rubric as { total_points?: unknown }

      if (currentRubric && typeof currentRubric.total_points === 'number') {
        updateCard(card.id, 'points', currentRubric.total_points)
      }
    }
    handleClose()
  }

  const currentChip = gradingOptions.find(opt => opt.value === selectedMethod)

  useEffect(() => {
    if (card?.grading_method !== selectedMethod) {
      setSelectedMethod(card?.grading_method)
    }
  }, [card?.grading_method])

  return (
    <Box alignItems="center" display="flex" gap={2}>
      <Chip
        icon={currentChip?.icon}
        label={currentChip?.chipLabel}
        sx={{
          cursor: 'pointer',
          fontWeight: 500,
          padding: '8px 16px 8px 8px',
          gap: '8px',
          borderRadius: '16px',
          background: '#F2F4F7',
          height: 42,
          '& .MuiChip-label': { fontSize: 14, p: 0 },
          '& .MuiChip-icon': { m: 0 },
        }}
        onClick={handleOpen}
      />

      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={Boolean(anchorEl)}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '16px',
            border: '1px solid #EAECF0',
            background: '#FFF',
            boxShadow: '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
          },
        }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClick={e => {
          e.preventDefault()
          e.stopPropagation()
        }}
        onClose={handleClose}
      >
        {gradingOptions.map(option => (
          <MenuItem
            key={option.value}
            selected={option.value === selectedMethod}
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()
            }}
          >
            <GradingMethodSelectorOptionWithPermission
              handleSelect={(value: string) => {
                handleSelect(value as 'rubric' | 'simple' | 'manual' | 'standards_based')
              }}
              option={option}
              requiredPermission={option.permission}
              selectedMethod={selectedMethod ?? 'manual'}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}

export function GradingMethodSelectorOptionWithPermission({
  requiredPermission,
  ...props
}: {
  requiredPermission: string | string[]
} & WithPermissionProps<GradingMethodSelectOptionProps>) {
  const Component = useMemo(
    () => withPermissionCheck(GradingMethodSelectOption, requiredPermission),
    [requiredPermission],
  )

  return <Component {...props} />
}

export default GradingMethodSelector
